// src/pages/Dashboard.jsx
import { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { getProfile, resumeSubscription } from '../api/services'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

export default function Dashboard() {
    const navigate = useNavigate()
    const location = useLocation()
    const { user: authUser, logout } = useAuth()

    // Core States
    const [user, setUser] = useState(authUser || null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [successMessage, setSuccessMessage] = useState(null)
    const [pendingCancellation, setPendingCancellation] = useState(false)
    const [currentPeriodEnd, setCurrentPeriodEnd] = useState(null)
    const [resuming, setResuming] = useState(false)

    // Live Greeting Structure
    const [currentTime, setCurrentTime] = useState(() => new Date())

    // Check for success message from navigation state
    useEffect(() => {
        if (location.state?.message) {
            setSuccessMessage(location.state.message)
            // Clear after 8 seconds
            setTimeout(() => setSuccessMessage(null), 8000)
            // Clear the state so it doesn't reappear on refresh
            window.history.replaceState({}, document.title)
        }
    }, [location])

    // Keep clock updated for long-running dashboard sessions
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 60000)
        return () => clearInterval(timer)
    }, [])

    const greeting = useMemo(() => {
        const hrs = currentTime.getHours()
        let timeOfDay = 'evening'
        if (hrs >= 4 && hrs < 12) timeOfDay = 'morning'
        else if (hrs >= 12 && hrs < 17) timeOfDay = 'afternoon'

        return {
            message: `Good ${timeOfDay}`,
            dateString: currentTime.toLocaleDateString('en-GB', {
                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
            })
        }
    }, [currentTime])

    // Format date for display
    const formatDate = (dateStr) => {
        if (!dateStr) return null
        try {
            return new Date(dateStr).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            })
        } catch {
            return null
        }
    }

    // Data Hydration Pipeline
    useEffect(() => {
        if (authUser) setUser(authUser)

        async function synchronizeDashboardContext() {
            setLoading(true)
            try {
                const profileResult = await getProfile()
                const data = profileResult.data
                setUser(data)
                setPendingCancellation(data.cancel_at_period_end || false)
                setCurrentPeriodEnd(data.current_period_end || null)
            } catch (err) {
                console.error("Profile retrieval failed:", err)
                if (!authUser) setError("Unable to resolve account parameters.")
            }
            setLoading(false)
        }

        synchronizeDashboardContext()
    }, [authUser])

    const metrics = useMemo(() => {
        // If monthly_cap is null/undefined, use a default or fallback
        const limit = user?.monthly_cap ?? 0
        const consumed = user?.usage_count ?? 0
        const remaining = Math.max(0, limit - consumed)
        const percentage = limit > 0
            ? Math.min(100, Math.round((consumed / limit) * 100))
            : 0

        let state = 'normal'
        if (limit === 0) state = 'no_limit'
        else if (remaining === 0) state = 'capped'
        else if (remaining <= 2) state = 'critical'
        else if (percentage >= 70 && percentage <= 90) state = 'warning'

        return { remaining, percentage, state, limit, consumed }
    }, [user])

    const displayName = useMemo(() => {
        const fname = user?.first_name || authUser?.first_name
        if (fname) return fname

        const email = user?.email || authUser?.email
        if (email) {
            const local = email.split('@')[0].replace(/[._\-]+/g, ' ').trim()
            if (local) return local.charAt(0).toUpperCase() + local.slice(1)
        }
        return 'there'
    }, [user, authUser])

    // ── Resume Subscription ─────────────────────────────────────────────
    const handleResumeSubscription = async () => {
        setResuming(true)
        try {
            const response = await resumeSubscription()
            const data = response.data

            setPendingCancellation(false)
            setCurrentPeriodEnd(null)
            setSuccessMessage('Your subscription has been resumed. Your plan will continue normally.')

            // Refresh user data
            const profileResult = await getProfile()
            setUser(profileResult.data)

            // Update localStorage
            const localUser = JSON.parse(localStorage.getItem('gmc_user') || '{}')
            localUser.cancel_at_period_end = false
            localUser.current_period_end = null
            localStorage.setItem('gmc_user', JSON.stringify(localUser))

        } catch (err) {
            setError('Failed to resume subscription. Please try again.')
        } finally {
            setResuming(false)
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col bg-[#0D1117]">
                <Navbar />
                <div className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8 animate-pulse">
                    <div className="h-12 bg-white/5 rounded-lg w-1/3" />
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-6">
                            <div className="h-36 bg-white/5 rounded-xl" />
                            <div className="h-10 bg-white/5 rounded-lg w-1/2" />
                        </div>
                        <div className="h-64 bg-white/5 rounded-xl" />
                    </div>
                </div>
                <Footer />
            </div>
        )
    }

    const isFreeTier = user?.plan_tier?.toLowerCase() === 'freemium'
    const isPaidTier = !isFreeTier && user?.plan_tier

    return (
        <div className="min-h-screen flex flex-col bg-[#0D1117] text-white font-sans antialiased selection:bg-brand-amber selection:text-[#0D1117]">
            <Navbar />

            <div className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
                {/* Header Area - Glassmorphism */}
                <div className="relative rounded-2xl overflow-hidden mb-8">
                    <div className="absolute inset-0"
                        style={{ background: 'linear-gradient(135deg, #0D1117 0%, #161B22 50%, #1C2333 100%)' }} />
                    <div className="absolute inset-0"
                        style={{
                            background: 'radial-gradient(ellipse 60% 50% at 0% 0%, rgba(212,168,67,0.06) 0%, transparent 60%)',
                        }} />

                    <div className="relative z-10 px-6 sm:px-8 py-8">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div>
                                <p className="text-[10px] font-bold tracking-[0.22em] uppercase text-brand-amber mb-2">
                                    Dashboard
                                </p>
                                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
                                    {greeting.message}, {displayName}.
                                </h1>
                                <p className="text-sm font-medium text-white/50 mt-1">
                                    Today is {greeting.dateString}. <span className="text-brand-amber font-semibold">How can GMC help you today?</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Success Message Toast */}
                {successMessage && (
                    <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-400 text-sm font-body">
                        <div className="flex items-start gap-3">
                            <svg className="w-5 h-5 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            <span>{successMessage}</span>
                        </div>
                    </div>
                )}

                {/* Error Message Toast */}
                {error && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm font-body">
                        <div className="flex items-start gap-3">
                            <svg className="w-5 h-5 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <span>{error}</span>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                    {/* Left/Center Column */}
                    <div className="lg:col-span-2 space-y-8">

                        {/* Usage Metrics Card - Glassmorphism */}
                        <div className={`relative rounded-2xl overflow-hidden transition-all duration-300
                            ${metrics.state === 'critical' ? 'ring-1 ring-brand-amber/30' : ''}
                            ${metrics.state === 'capped' ? 'ring-1 ring-red-500/30' : ''}
                            ${metrics.state === 'no_limit' ? 'ring-1 ring-emerald-500/30' : ''}
                        `}>
                            <div className="absolute inset-0"
                                style={{ background: 'linear-gradient(135deg, #161B22 0%, #1C2333 100%)' }} />
                            <div className="absolute inset-0"
                                style={{
                                    background: 'radial-gradient(ellipse 60% 40% at 100% 100%, rgba(212,168,67,0.04) 0%, transparent 60%)',
                                }} />

                            {metrics.state === 'critical' && (
                                <div className="absolute top-0 left-0 right-0 h-1 bg-brand-amber animate-pulse" />
                            )}
                            {metrics.state === 'capped' && (
                                <div className="absolute top-0 left-0 right-0 h-1 bg-red-500" />
                            )}

                            <div className="relative z-10 p-6 sm:p-8">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-2">
                                    <div>
                                        <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-white/40 block">
                                            Usage Status
                                        </span>
                                        {metrics.limit === 0 ? (
                                            <h2 className="text-xl font-bold text-white mt-0.5">
                                                Unlimited analyses this cycle
                                            </h2>
                                        ) : (
                                            <h2 className="text-xl font-bold text-white mt-0.5">
                                                {metrics.remaining} analyses remaining this cycle
                                            </h2>
                                        )}
                                    </div>
                                    {metrics.limit > 0 && (
                                        <div>
                                            <span className="text-xs font-mono font-bold text-white/60 bg-white/5 border border-white/10 px-3 py-1.5 rounded-lg">
                                                {metrics.consumed} / {metrics.limit} used
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {metrics.limit > 0 ? (
                                    <>
                                        <div className="w-full bg-white/10 rounded-full h-2.5 overflow-hidden mb-4">
                                            <div
                                                className={`h-full transition-all duration-1000 ease-out
                                                    ${metrics.state === 'normal' ? 'bg-brand-amber' : ''}
                                                    ${metrics.state === 'warning' ? 'bg-amber-500' : ''}
                                                    ${metrics.state === 'critical' ? 'bg-amber-500 animate-pulse' : ''}
                                                    ${metrics.state === 'capped' ? 'bg-red-500' : ''}
                                                `}
                                                style={{ width: `${metrics.percentage}%` }}
                                            />
                                        </div>

                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-1">
                                            <p className="text-xs text-white/50 font-medium">
                                                {metrics.state === 'normal' && "Your account usage tracking is normal."}
                                                {metrics.state === 'warning' && `Your plan includes ${metrics.remaining} more analyses this cycle.`}
                                                {metrics.state === 'critical' && "⚠️ Quota threshold low. Upgrade to continue uninterrupted →"}
                                                {metrics.state === 'capped' && "❌ Allocation cap reached. Please upgrade to continue."}
                                            </p>
                                            <span className="text-xs font-semibold text-white/30">
                                                {user?.plan_tier?.toLowerCase() === 'freemium'
                                                    ? "Account Limit Active"
                                                    : `Resets on ${new Date(user?.cycle_reset_date || Date.now() + 24 * 60 * 60 * 1000 * 25).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`
                                                }
                                            </span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-1">
                                        <p className="text-xs text-white/50 font-medium">
                                            🎉 You're on an unlimited plan. No usage caps apply.
                                        </p>
                                    </div>
                                )}

                                <div className="mt-6 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
                                    {metrics.state === 'capped' ? (
                                        <div className="w-full p-4 bg-white/5 border border-brand-amber/20 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                            <div className="space-y-0.5">
                                                <p className="text-xs font-bold text-white">Free execution limit reached</p>
                                                <p className="text-[11px] text-white/50">Purchase a single on-demand pass or activate a premium subscription.</p>
                                            </div>
                                            <Link to="/plans" className="w-full sm:w-auto px-5 py-2.5 bg-brand-amber hover:bg-brand-amber2 text-[#0D1117] text-xs font-bold rounded-lg transition-all text-center shadow-sm flex-shrink-0">
                                                Upgrade Plan
                                            </Link>
                                        </div>
                                    ) : (
                                        <>
                                            <Link to="/analyze" className="w-full sm:w-auto px-6 py-3 bg-brand-amber hover:bg-brand-amber2 text-[#0D1117] rounded-lg text-xs font-bold tracking-wide transition-all shadow-sm flex items-center justify-center gap-2">
                                                <span>Start New Analysis</span>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <line x1="5" y1="12" x2="19" y2="12" />
                                                    <polyline points="12 5 19 12 12 19" />
                                                </svg>
                                            </Link>
                                            {metrics.state === 'critical' && (
                                                <Link to="/plans" className="text-xs font-bold text-brand-amber hover:text-brand-amber2 transition-colors">
                                                    Upgrade Now →
                                                </Link>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Zero Retention Banner - Glassmorphism */}
                        <div className="relative rounded-2xl overflow-hidden">
                            <div className="absolute inset-0"
                                style={{ background: 'linear-gradient(135deg, #161B22 0%, #1C2333 100%)' }} />
                            <div className="absolute inset-0"
                                style={{
                                    background: 'radial-gradient(ellipse 50% 80% at 100% 50%, rgba(212,168,67,0.03) 0%, transparent 60%)',
                                }} />

                            <div className="relative z-10 px-6 sm:px-8 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-brand-amber">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-white tracking-wide">
                                            🔒 Zero Message Retention Active
                                        </p>
                                        <p className="text-xs text-white/40 font-light">
                                            Your messages are never stored, logged, or used for training.
                                        </p>
                                    </div>
                                </div>
                                <span className="text-[10px] font-mono font-bold text-emerald-400 bg-white/5 border border-white/10 px-3 py-1.5 rounded-lg uppercase tracking-wider">
                                    Verified
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Right Sidebar */}
                    <div className="space-y-6">
                        {/* Plan Card - Glassmorphism */}
                        <div className="relative rounded-2xl overflow-hidden">
                            <div className="absolute inset-0"
                                style={{ background: 'linear-gradient(135deg, #161B22 0%, #1C2333 100%)' }} />
                            <div className="absolute inset-0"
                                style={{
                                    background: 'radial-gradient(ellipse 60% 40% at 0% 100%, rgba(212,168,67,0.04) 0%, transparent 60%)',
                                }} />

                            <div className="relative z-10 p-6">
                                <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-white/40 block mb-3">
                                    Plan
                                </span>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-base font-bold text-white capitalize">
                                        {user?.plan_tier || 'Freemium'}
                                    </h3>
                                    {pendingCancellation ? (
                                        <span className="px-3 py-1 bg-amber-500/20 border border-amber-500/30 text-amber-400 text-[10px] font-bold uppercase rounded-lg tracking-wider">
                                            Cancelling
                                        </span>
                                    ) : isPaidTier ? (
                                        <span className="px-3 py-1 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-[10px] font-bold uppercase rounded-lg tracking-wider">
                                            Active
                                        </span>
                                    ) : (
                                        <span className="px-3 py-1 bg-brand-amber/20 border border-brand-amber/30 text-brand-amber text-[10px] font-bold uppercase rounded-lg tracking-wider">
                                            Active
                                        </span>
                                    )}
                                </div>

                                {/* Pending cancellation notice */}
                                {pendingCancellation && currentPeriodEnd && (
                                    <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                                        <p className="text-amber-400 text-xs font-body">
                                            Your plan will downgrade to Free on <strong>{formatDate(currentPeriodEnd)}</strong>
                                        </p>
                                        <button
                                            onClick={handleResumeSubscription}
                                            disabled={resuming}
                                            className="mt-2 text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors disabled:opacity-50"
                                        >
                                            {resuming ? 'Resuming...' : 'Resume subscription →'}
                                        </button>
                                    </div>
                                )}

                                {/* Show limit on sidebar */}
                                {user?.monthly_cap !== undefined && user?.monthly_cap !== null && user?.monthly_cap > 0 && (
                                    <div className="mb-4">
                                        <div className="flex justify-between text-xs text-white/50 mb-1">
                                            <span>Monthly limit</span>
                                            <span>{user.monthly_cap} analyses</span>
                                        </div>
                                        <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-brand-amber rounded-full transition-all duration-500"
                                                style={{ width: `${Math.min((user.usage_count || 0) / user.monthly_cap * 100, 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="border-t border-white/10 pt-4">
                                    <Link to="/plans" className="w-full flex items-center justify-between p-2 hover:bg-white/5 rounded-lg text-xs font-semibold text-white/70 transition-all">
                                        <span>{pendingCancellation ? 'View Plans' : 'Change Plan'}</span>
                                        <span className="text-white/30">→</span>
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions Card - Glassmorphism */}
                        <div className="relative rounded-2xl overflow-hidden">
                            <div className="absolute inset-0"
                                style={{ background: 'linear-gradient(135deg, #161B22 0%, #1C2333 100%)' }} />

                            <div className="relative z-10 p-6">
                                <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-white/40 block mb-4">
                                    Quick Actions
                                </span>
                                <div className="space-y-1">
                                    <Link to="/analyze" className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-xl text-sm font-semibold text-white/80 transition-all group">
                                        <span className="text-lg">✏️</span>
                                        <span>Analyze a Message</span>
                                        <span className="ml-auto text-white/20 group-hover:text-brand-amber transition-colors">→</span>
                                    </Link>
                                    <Link to="/profile" className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-xl text-sm font-semibold text-white/80 transition-all group">
                                        <span className="text-lg">👤</span>
                                        <span>Profile Settings</span>
                                        <span className="ml-auto text-white/20 group-hover:text-brand-amber transition-colors">→</span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    )
}
// End of Dashboard.jsx